/**
 * PAGE PROMO PRODUIT — /promo/:slug  (PUBLIC, partageable réseaux sociaux)
 * Landing premium autonome d'un seul produit, avec achat direct (Stripe).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { toast } from "sonner";
import { CircleNotch, ArrowRight, ShieldCheck, Truck, Package } from "@phosphor-icons/react";

const pub = axios.create({ baseURL: API });
const LOGO_WHITE = "/logos/master_text_sm.webp";

export default function PromoProduit() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [state, setState] = useState("loading");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    pub.get(`/dropship/products/${slug}`)
      .then((r) => { setP(r.data); setState("ready"); })
      .catch(() => setState("notfound"));
  }, [slug]);

  async function buy() {
    if (!email.trim()) return toast.error("Entre ton courriel pour continuer");
    setBusy(true);
    try {
      const { data } = await pub.post("/dropship/checkout", {
        items: [{ product_id: p.id, quantity: 1 }], customer_email: email.trim(), customer_name: name.trim(),
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
      else toast.error("Paiement indisponible");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur de paiement"); }
    setBusy(false);
  }

  if (state === "loading") {
    return <div className="kz-body min-h-screen flex items-center justify-center bg-[#0A0A0A]"><CircleNotch size={36} className="text-[#D90429] animate-spin" /></div>;
  }
  if (state === "notfound") {
    return (
      <div className="kz-body min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white p-6 text-center" data-testid="promo-notfound">
        <div>
          <Package size={48} className="text-white/40 mx-auto" />
          <p className="kz-display text-2xl mt-4">Produit introuvable</p>
          <a href="/store" className="inline-block mt-6 bg-white text-black px-7 py-3 font-bold tracking-wider hover:bg-[#D90429] hover:text-white transition-colors">Voir la boutique</a>
        </div>
      </div>
    );
  }

  return (
    <div className="kz-body min-h-screen bg-[#0A0A0A] text-white grid lg:grid-cols-2" data-testid="promo-produit">
      {/* Visuel produit */}
      <div className="relative bg-zinc-900 flex items-center justify-center p-8 min-h-[40vh] lg:min-h-screen">
        <img src={LOGO_WHITE} alt="Institut Impact Élite Martial" className="absolute top-6 left-6 h-12 object-contain opacity-90" />
        {p.image_url ? (
          <img src={p.image_url} alt={p.name} className="max-h-[70vh] w-auto object-contain" data-testid="promo-product-image" />
        ) : (
          <Package size={120} className="text-white/10" />
        )}
        {p.brand && <span className="absolute bottom-6 left-6 kz-mono text-[10px] tracking-[0.22em] uppercase text-white/50 border border-white/15 px-3 py-1.5">{p.brand}</span>}
      </div>

      {/* Achat */}
      <div className="flex items-center p-6 sm:p-12 lg:p-16">
        <div className="w-full max-w-md kz-fade-up">
          <span className="kz-mono text-[10px] tracking-[0.26em] uppercase text-[#D90429]">Boutique Institut Impact</span>
          <h1 className="kz-display text-4xl sm:text-5xl font-medium mt-3 leading-[0.98]">{p.name}</h1>
          <div className="kz-display text-3xl mt-4 text-[#D90429]">{Number(p.price_cad).toLocaleString("fr-CA")} $ <span className="text-white/40 text-base kz-body">CAD</span></div>
          {p.description && <p className="kz-body text-white/60 mt-5 leading-relaxed">{p.description}</p>}

          <div className="mt-8 space-y-3">
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Ton nom"
              data-testid="promo-buy-name" className="w-full bg-transparent border-b border-white/20 focus:border-[#D90429] outline-none text-white placeholder-white/30 py-2.5" />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Ton courriel"
              data-testid="promo-buy-email" className="w-full bg-transparent border-b border-white/20 focus:border-[#D90429] outline-none text-white placeholder-white/30 py-2.5" />
          </div>

          <button onClick={buy} disabled={busy} data-testid="promo-buy-btn"
            className="w-full mt-7 bg-[#D90429] text-white py-4 font-bold tracking-wider hover:bg-white hover:text-black transition-colors flex items-center justify-center gap-3 disabled:opacity-50">
            {busy ? <CircleNotch size={18} className="animate-spin" /> : <>Acheter maintenant <ArrowRight size={18} weight="bold" /></>}
          </button>

          <div className="flex items-center gap-6 mt-6 text-white/40 text-xs">
            <span className="flex items-center gap-1.5"><ShieldCheck size={15} /> Paiement sécurisé Stripe</span>
            <span className="flex items-center gap-1.5"><Truck size={15} /> Cueillette / livraison</span>
          </div>
          <p className="kz-mono text-[10px] tracking-[0.18em] uppercase text-white/25 mt-8">Taxes (TPS + TVQ) calculées au paiement</p>
        </div>
      </div>
    </div>
  );
}
